/* eslint-disable @typescript-eslint/indent */
import { type DoctorType } from '../../../../model/doctor'
import {
  getCurrentMonthNumber,
  getCurrentYear
} from '../../../../utils/dateUtils'

function finiteNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function epochMillis(value: unknown, fallback = 0): number {
  if (value && typeof value === 'object' && 'toMillis' in value) {
    const toMillis = (value as { toMillis?: unknown }).toMillis
    if (typeof toMillis === 'function') {
      const parsed = Number(toMillis.call(value))
      if (Number.isFinite(parsed)) return parsed
    }
  }
  if (value instanceof Date) {
    const parsed = value.getTime()
    return Number.isFinite(parsed) ? parsed : fallback
  }
  const numeric = Number(value)
  if (Number.isFinite(numeric)) return numeric
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

export default function useAccountSubscription({
  isSubscribed,
  statistics,
  planExpiresAt,
  freeMaxUsage,
  accountAccess
}: Pick<
  DoctorType,
  | 'isSubscribed'
  | 'statistics'
  | 'planExpiresAt'
  | 'freeMaxUsage'
  | 'accountAccess'
>) {
  const now = Date.now()
  const year = getCurrentYear()
  const month = getCurrentMonthNumber()
  const currentMonthUsage =
    Number(statistics.visits?.[year]?.[month] ?? 0) +
    Number(statistics.followUps?.[year]?.[month] ?? 0)
  const usesV2 = accountAccess?.schemaVersion === 2
  const legacyUsage =
    finiteNumber(statistics.visits.total) +
    finiteNumber(statistics.followUps.total)
  const usage = usesV2 ? currentMonthUsage : legacyUsage
  const freeLimit = finiteNumber(
    usesV2 ? accountAccess.freeMonthlyLimit : freeMaxUsage
  )
  const timedAccessUntil = epochMillis(
    usesV2 ? accountAccess.timedAccessUntil : planExpiresAt
  )
  const hasTimedAccess = timedAccessUntil > now && (usesV2 || isSubscribed)
  const isSuspended = usesV2 && accountAccess.suspended

  const isFreeMaxUsageReached =
    !hasTimedAccess && (usesV2 || !isSubscribed) && usage >= freeLimit
  const isLegacyPlanExpired = !usesV2 && isSubscribed && !hasTimedAccess
  const isAccessBlocked =
    isSuspended || isLegacyPlanExpired || isFreeMaxUsageReached

  return {
    isFreeMaxUsageReached,
    isPlanExpired: isLegacyPlanExpired,
    isAccessBlocked,
    isSuspended,
    hasTimedAccess,
    timedAccessUntil,
    freeMonthlyLimit: freeLimit,
    monthlyUsage: usage
  }
}
